import React, { useRef, useEffect } from "react";
import { $Button } from "us.common/components";
import "./MessageInput.scss";
import { useField } from "formik";
import TextArea from "antd/lib/input/TextArea";
import { SendOutlined } from "us.icons";
import { useTranslation } from "react-i18next";
import { URLType } from "us.common/constants";
import {
  ReloadOutlined,
} from "us.icons";

type MessageInputProps = {
  caseType: URLType;
  value: string;
  onSubmit: () => void;
  disabled?: boolean;
  onClickRefreshSummary: () => void;
};

/**
 * MessageInput is a controlled input component for composing and submitting chat messages.
 *
 * @param value - The current value of the message input.
 * @param onSubmit - Callback function invoked when the message is submitted.
 * @param disabled - Optional flag to disable the input and send button. Defaults to `false`.
 *
 * The component uses Formik's `useField` hook to manage the message state and validation.
 * It renders a resizable text area and a send button. The send button is disabled if the input is empty or disabled.
 */
const MessageInput: React.FC<MessageInputProps> = ({
  value,
  caseType,
  onSubmit,
  disabled = false,
  onClickRefreshSummary
}) => {
  const { t } = useTranslation("US.COMMON");
  const [field, meta, helpers] = useField("message");
  const textAreaRef = useRef<any>(null);

  // Initial focus when component mounts
  useEffect(() => {
    if (textAreaRef.current) {
      textAreaRef.current.focus();
    }
  }, []);

  // Maintain focus when disabled state changes (after sending message)
  useEffect(() => {
    if (!disabled && textAreaRef.current) {
      const timer = setTimeout(() => {
        textAreaRef.current.focus();
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [disabled]);

  // Maintain focus after form reset (when field value becomes empty)
  useEffect(() => {
    if (field.value === "" && !disabled && textAreaRef.current) {
      const timer = setTimeout(() => {
        textAreaRef.current.focus();
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [field.value, disabled]);

  const handleSubmit = () => {
    if (field.value.trim() && !disabled) {
      onSubmit();
      // Ensure focus returns after submit
      setTimeout(() => {
        if (textAreaRef.current && !disabled) {
          textAreaRef.current.focus();
        }
      }, 100);
    }
  };

  const handleTextAreaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    helpers.setValue(e.target.value);
  };

  return (
    <div className="chat-input">
      {caseType == URLType.CASE && (
        <div className="mb-1">
          <$Button
            type="link"
            icon={<ReloadOutlined />}
            onClick={onClickRefreshSummary}
            disabled={disabled}
            size="small"
          >
            {t("US.COMMON:UNIQ.REFRESH_CASE_SUMMARY")}
          </$Button>
        </div>
      )}
      <div className="input-container">
        <TextArea
          ref={textAreaRef}
          name="message"
          value={field.value}
          onChange={handleTextAreaChange}
          placeholder={t("US.COMMON:UNIQ.TYPE_YOUR_MESSAGE")}
          autoSize={{ minRows: 1, maxRows: 3 }}
          maxLength={200}
          disabled={disabled}
          className="message-input"
          onPressEnter={handleSubmit}
          autoFocus
          spellCheck
        />
        <$Button
          type="primary"
          onClick={handleSubmit}
          disabled={!field.value.trim() || disabled}
          className="send-button"
          icon={<SendOutlined />}
        ></$Button>
      </div>
    </div>
  );
};

export default MessageInput;
